import React, { useState, useRef, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import notify from 'devextreme/ui/notify';
import { Button } from 'devextreme-react/button';
import { SelectBox } from 'devextreme-react/select-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import FormField from '../../../dynamicFormBuilder/FormField';
import {
    DataGrid,
    Column,
    Editing,
    Lookup,
    Paging,
    Pager,
    FilterRow,
    Sorting,
    Scrolling,
    ColumnChooser,
    ColumnChooserSearch,
} from 'devextreme-react/data-grid';
import data from '../../../assets/apiRoutes.json';
import { HeaderLayout } from './Layout';
import { TransferQtyFlag } from '../../../const/constants';
import '../../../common/css/searchPage.css';
import './moveSublot.css';

const routes = data.MoveSublotInventory;
const LIST_ROUTE = '/wm/move-sublot-inventory';

// Transfer option flags mirror the backend Constants.TransferQtyFlag (All / Fixed Qty / Percent).
const transferOptions = [
    { id: TransferQtyFlag.All, text: 'All' },
    { id: TransferQtyFlag.Fixed, text: 'Fixed Qty' },
    { id: TransferQtyFlag.Percent, text: 'Percent' },
];

const initialHeader = {
    PROFILE: '',
    TRANSACTIONNUMBER: '',
    SHIPPERBOLNUMBER: '',
    REFERENCE: '',
    TRANSACTIONGROUP: '',
    PLANREFERENCE: '',
    EQUIPMENTINTERCHANGEREPORT: '',
    TRANSACTIONDATE: null,
    ACTUALRECEIVEDSHIPPEDDATE: null,
    DELIVERYPICKUPDEPARTDATETIME: null,
    R_GLDEPARTMENT: null,
    R_CAJOB: null,
    R_GLFUND: null,
    COMMENT: '',
};

// Per-row editor for the To Lot column: loads the lots the row's item can move
// into (same item, excluding the source lot) and writes the chosen lot back to
// the row so the read cell can show its code.
const ToLotEditCell = ({ cellInfo, accessToken }) => {
    const [lots, setLots] = useState([]);
    React.useEffect(() => {
        const itemRowId = cellInfo.data.ItemRowId;
        const excludeLotRowId = cellInfo.data.LotRowId;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.GetToLots}?itemRowId=${itemRowId}&excludeLotRowId=${excludeLotRowId}`, {
                headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => setLots(res.data || []))
            .catch(() => setLots([]));
    }, []);
    return (
        <SelectBox
            dataSource={lots}
            valueExpr="id"
            displayExpr="name"
            defaultValue={cellInfo.value}
            searchEnabled={true}
            placeholder="Select To Lot..."
            onValueChanged={(e) => {
                const lot = lots.find((l) => l.id === e.value);
                cellInfo.setValue(e.value);
                cellInfo.data.ToLotCode = lot ? lot.name : '';
            }}
        />
    );
};

const MoveSublotInventory = (props) => {
    const accessToken = props.oidc.user.access_token;
    const currentUser = props.oidc.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const history = useHistory();
    const location = useLocation();
    const formRefs = useRef({});
    const dataGrid = useRef(null);

    // Sublots selected on the List (Find) screen are handed over via router state.
    const incoming = (location.state && location.state.sublots) || [];
    const gridDataRef = useRef(
        incoming.map((r) => ({
            ...r,
            Selected: true,
            ToLotRowId: null,
            ToLotCode: '',
            TransferOption: TransferQtyFlag.All,
            TransferQty: null,
        }))
    );
    const [gridData] = useState(gridDataRef.current);
    const [header, setHeader] = useState(initialHeader);
    const [loading, setLoading] = useState(false);

    const position = { of: '#accordion' };

    // Load the customer's WM Adjustment Profile once for the read-only Profile display. All selected
    // rows are a single customer (enforced on Find + Move), so the first row's customer is authoritative.
    useEffect(() => {
        const customerRowId = gridDataRef.current[0] && gridDataRef.current[0].RIdentity;
        if (!customerRowId) return;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.GetAdjustmentProfile}?customerRowId=${customerRowId}`, {
                headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                const p = res.data;
                if (!p) return;
                const display = p.description ? `${p.name} - ${p.description}` : (p.name || '');
                setHeader((h) => ({ ...h, PROFILE: display }));
            })
            .catch(() => { /* leave Profile blank if it cannot be loaded */ });
    }, [accessToken]);

    // Header fields are pushed into state by FormField itself; no extra work here.
    const handleFieldChange = useCallback((name, value) => { }, []);
    const handleFormLoad = useCallback(() => { }, []);

    const goToList = () => history.push(LIST_ROUTE);

    const onMoveInventory = async () => {
        // Commit any open cell edit so the row objects reflect the latest values.
        if (dataGrid.current && dataGrid.current.instance) {
            await dataGrid.current.instance().saveEditData();
        }
        const selected = gridDataRef.current.filter((r) => r.Selected === true);
        if (selected.length === 0) {
            notify({ message: 'Please select at least one record.', width: 450 }, 'error', 2500);
            return;
        }
        const distinctCustomers = [...new Set(selected.map((r) => r.RIdentity))];
        if (distinctCustomers.length > 1) {
            notify({ message: 'Please select records from a single customer.', width: 450 }, 'error', 2500);
            return;
        }
        if (selected.some((r) => !r.ToLotRowId)) {
            notify({ message: 'To Lot is not selected for one or more records.', width: 450 }, 'error', 2500);
            return;
        }
        // Fixed Qty / Percent require a valid Transfer Qty; otherwise the server silently
        // computes 0 and skips the row. Flag it here with a targeted message.
        const badQty = selected.find((r) => {
            if (r.TransferOption === TransferQtyFlag.Fixed) return r.TransferQty == null || r.TransferQty <= 0;
            if (r.TransferOption === TransferQtyFlag.Percent) return r.TransferQty == null || r.TransferQty <= 0 || r.TransferQty > 100;
            return false;
        });
        if (badQty) {
            notify({
                message: `Enter a valid Transfer Qty for ${badQty.ItemCode} / ${badQty.LotCode} / ${badQty.SublotCode} — Fixed Qty must be greater than 0; Percent must be between 1 and 100.`,
                width: 500,
            }, 'error', 4000);
            return;
        }
        // Every transfer moves from on-hand, so a row with no On Hand has nothing to move. If none of the
        // selected rows has on-hand, block upfront and name them (the server guards this too, but its
        // on-hand is computed from location summaries, which can differ from the grid's On Hand).
        const noOnHand = selected.filter((r) => !(r.OnHandSublot > 0));
        if (noOnHand.length === selected.length) {
            notify({
                message: `The selected sublot(s) have no On Hand quantity to move: ${noOnHand.map((r) => r.SublotCode).join(', ')}.`,
                width: 550,
            }, 'error', 4000);
            return;
        }

        const request = {
            customerRowId: selected[0].RIdentity,
            moves: selected.map((r) => ({
                key: r.Key,
                itemRowId: r.ItemRowId,
                fromLotRowId: r.LotRowId,
                fromSublotRowId: r.SublotRowId,
                toLotRowId: r.ToLotRowId,
                transferOption: r.TransferOption || TransferQtyFlag.All,
                transferQty: r.TransferQty,
            })),
            transactionNumber: header.TRANSACTIONNUMBER,
            shipperBolNumber: header.SHIPPERBOLNUMBER,
            reference: header.REFERENCE,
            transactionGroup: header.TRANSACTIONGROUP,
            planReference: header.PLANREFERENCE,
            equipmentInterchangeReport: header.EQUIPMENTINTERCHANGEREPORT,
            transactionDate: header.TRANSACTIONDATE,
            actualReceivedShippedDate: header.ACTUALRECEIVEDSHIPPEDDATE,
            deliveryPickupDepartDate: header.DELIVERYPICKUPDEPARTDATETIME,
            department: header.R_GLDEPARTMENT || null,
            job: header.R_CAJOB || null,
            fund: header.R_GLFUND || null,
            comment: header.COMMENT,
        };

        setLoading(true);
        axios
            .post(`${window._env_.REACT_APP_APIS}/${routes.MoveInventory}`, request, {
                headers: {
                    'Content-Type': 'application/json',
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                },
            })
            .then((res) => {
                setLoading(false);
                if (res.data && res.data.success) {
                    notify({ message: res.data.message || 'Adjustment transaction created.', width: 450 }, 'success', 3000);
                    // A successful move returns to the Find listing (green toast + redirect).
                    goToList();
                } else {
                    notify({ message: (res.data && res.data.message) || 'Could not create the adjustment transaction.', width: 450 }, 'error', 3500);
                }
            })
            .catch(() => {
                setLoading(false);
                notify({ message: 'Some error has occurred while processing.', width: 450 }, 'error', 3000);
            });
    };

    if (incoming.length === 0) {
        // Reached directly (e.g. refresh) without a selection — send the user back to Find.
        return (
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion" style={{ padding: '20px' }}>
                        <h6 style={{ color: window._env_.Color2 }}>Move Sublot Inventory</h6>
                        <p>No sublots selected. Use Find to select the sublots to move.</p>
                        <Button type="default" text="Go to Find" onClick={goToList} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="search-page-container">
            <div className="search-page-content" id="accordion">
                    <LoadPanel
                        shadingColor="rgba(0,0,0,0.4)"
                        position={position}
                        visible={loading}
                        showIndicator={true}
                        shading={true}
                        showPane={true}
                        hideOnOutsideClick={false}
                    />
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6
                                    style={{
                                        color: window._env_.Color2,
                                        marginLeft: '12px'
                                    }}
                                >
                                    Move Sublot Inventory
                                </h6>
                            </div>
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={onMoveInventory} text="Move Inventory" type="default" />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={goToList} text="Back to Find" />
                        </Item>
                    </Toolbar>

                    <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <TabPanel showNavButtons={true} height="100%">
                        <TabItem title="Primary">
                            {/* Header fields shared across every selected row. */}
                            <FormField
                                currrentUser={currentUser}
                                layoutId='5C2F8B1D-9A3E-4D67-B0F4-3E7A1C9D6B25'
                                layoutName='WMMoveSublotInventoryHeaderForm'
                                defaultFormSchema={HeaderLayout}
                                formFields={header}
                                setformFields={setHeader}
                                handleFieldValueChange={handleFieldChange}
                                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                                formRefs={formRefs}
                                onFormLoad={handleFormLoad}
                            />
                        </TabItem>
                        <TabItem title="Details">
                            <DataGrid
                                ref={dataGrid}
                                dataSource={gridData}
                                keyExpr="Key"
                                className="search-page-grid move-sublot-detail-grid"
                                showBorders={true}
                                hoverStateEnabled={true}
                                allowColumnReordering={true}
                                allowColumnResizing={true}
                                columnAutoWidth={true}
                                repaintChangesOnly={true}
                                height="100%"
                            >
                                <Scrolling mode="standard" />
                                <Sorting mode="multiple" />
                                <FilterRow visible={true} />
                                <Paging defaultPageSize={20} />
                                <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 20, 100, 'all']} showNavigationButtons={true} />
                                <Editing mode="cell" allowUpdating={true} />
                                <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                                    <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
                                </ColumnChooser>

                                {/* Selection + per-row target fields. */}
                                <Column dataField="Selected" caption="Select" dataType="boolean" width={70} allowEditing={true} />
                                <Column
                                    dataField="ToLotRowId"
                                    caption="To Lot"
                                    allowEditing={true}
                                    minWidth={160}
                                    cellRender={(c) => c.data.ToLotCode || ''}
                                    editCellRender={(cellInfo) => <ToLotEditCell cellInfo={cellInfo} accessToken={accessToken} />}
                                />
                                <Column dataField="TransferOption" caption="Transfer Option" allowEditing={true} width={130}>
                                    <Lookup dataSource={transferOptions} valueExpr="id" displayExpr="text" />
                                </Column>
                                <Column dataField="TransferQty" caption="Transfer Qty" dataType="number" allowEditing={true} width={110} />

                                {/* Read-only inventory columns — same set as the Find (search) grid,
                                    carried over from the selection. Kept read-only here (editing is only
                                    for Select / To Lot / Transfer Option / Transfer Qty). */}
                                <Column dataType="string" caption="Customer" dataField="CustomerName" allowEditing={false} />
                                <Column dataType="string" caption="Customer Code" dataField="Customer" allowEditing={false} visible={false} />
                                <Column dataType="string" caption="Item" dataField="ItemCode" allowEditing={false} />
                                <Column dataType="string" caption="Lot" dataField="LotCode" allowEditing={false} />
                                <Column dataType="string" caption="Sublot" dataField="SublotCode" allowEditing={false} />
                                <Column dataType="string" caption="Description1" dataField="SublotDescription1" allowEditing={false} />
                                <Column dataType="string" caption="Status" dataField="StatusSublot" allowEditing={false}>
                                    <Lookup
                                        dataSource={[{ id: 'A', text: 'Active' }, { id: 'I', text: 'Inactive' }]}
                                        displayExpr="text"
                                        valueExpr="id" />
                                </Column>
                                <Column dataType="date" caption="Date Available" dataField="DateAvailableSublot" format="MM/dd/yyyy" allowEditing={false} />
                                <Column dataType="number" caption="Net Available" dataField="NetAvailableSublot" format="#,##0.00" allowEditing={false} />
                                <Column dataType="number" caption="Available" dataField="SublotAvailable" format="#,##0.00" allowEditing={false} />
                                <Column dataType="number" caption="On Hand" dataField="OnHandSublot" format="#,##0.00" allowEditing={false} />
                                <Column dataType="string" caption="Record #" dataField="RecordNumber" allowEditing={false} />

                                {/* Customer's WM Adjustment Profile (legacy AdjProfile). Available via chooser. */}
                                <Column dataType="string" caption="Adjustment Profile" dataField="AdjProfile" allowEditing={false} visible={false} />

                                {/* Additional VW_WMSUBLOTVIEW buckets, hidden by default (parity with Find). */}
                                <Column dataType="string" caption="Description2" dataField="SublotDescription2" allowEditing={false} visible={false} />
                                <Column dataType="date" caption="Date Introduced" dataField="SublotDateIntroduced" format="MM/dd/yyyy" allowEditing={false} visible={false} />
                                <Column dataType="date" caption="Date Expiration" dataField="DateExpirationSublot" format="MM/dd/yyyy" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Net Weight" dataField="NetWeightQuantitySublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Gross Weight" dataField="GrossWeightQuantitySublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="In Receiving" dataField="InReceivingSublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="In Shipping" dataField="InShippingSublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Pending Rec" dataField="PendingRecSublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Pending Ship" dataField="PendingShipSublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Future Rec" dataField="FutureRecSublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Future Ship" dataField="FutureShipSublot" format="#,##0.00" allowEditing={false} visible={false} />
                                <Column dataType="number" caption="Unavailable" dataField="UnavailableSublot" format="#,##0.00" allowEditing={false} visible={false} />

                                {/* Technical key columns hidden per convention. */}
                                <Column dataField="ItemRowId" visible={false} showInColumnChooser={false} />
                                <Column dataField="LotRowId" visible={false} showInColumnChooser={false} />
                                <Column dataField="SublotRowId" visible={false} showInColumnChooser={false} />
                                <Column dataField="RIdentity" visible={false} showInColumnChooser={false} />
                                <Column dataField="ItemType" visible={false} showInColumnChooser={false} />
                            </DataGrid>
                        </TabItem>
                    </TabPanel>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(MoveSublotInventory);
